import {
  EyeSlashIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import cn from "classnames";
import { Editor as EditorType } from "@tiptap/react";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import Skeleton from "react-loading-skeleton";
import { useAuth } from "../../context/AuthContext";
import { RedactionType } from "../../db/schema";
import Button from "../common/Button";
import Toggle from "../Settings/Toggle";
import { toastOptions } from "../common/toastOptions";

interface RedactionsProps {
  caseRedactions?: RedactionType[];
  setCaseRedactions?: React.Dispatch<React.SetStateAction<RedactionType[]>>;
  caseId?: string;
  editorContent?: string;
  editor?: EditorType | null;
  isCaseLoading?: boolean;
  // Lifted to the parent so the bubble-menu Redact/Unredact actions and this
  // toggle stay in sync (one source of truth for the editor highlight).
  highlightEnabled: boolean;
  setHighlightEnabled: React.Dispatch<React.SetStateAction<boolean>>;
}

const CATEGORY_OPTIONS: RedactionType["category"][] = [
  "Name",
  "Address",
  "Phone",
  "Email",
  "SSN",
  "DOB",
  "License Plate",
  "Driver's License",
  "Vehicle",
  "Juvenile",
  "Medical",
  "Financial",
  "Other",
];

const CATEGORY_COLORS: Record<RedactionType["category"], string> = {
  Name: "bg-blue-100 text-blue-800 dark:bg-blue-900/60 dark:text-blue-300",
  Address: "bg-purple-100 text-purple-800 dark:bg-purple-950/40 dark:text-purple-400",
  Phone: "bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-400",
  Email: "bg-teal-100 text-teal-800 dark:bg-teal-950/40 dark:text-teal-400",
  SSN: "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-400",
  DOB: "bg-pink-100 text-pink-800 dark:bg-pink-950/40 dark:text-pink-400",
  "License Plate": "bg-yellow-100 text-yellow-800 dark:bg-yellow-950/40 dark:text-amber-400",
  "Driver's License": "bg-orange-100 text-orange-800 dark:bg-orange-950/40 dark:text-orange-400",
  Vehicle: "bg-indigo-100 text-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-400",
  Juvenile: "bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-400",
  Medical: "bg-cyan-100 text-cyan-800 dark:bg-cyan-950/40 dark:text-cyan-400",
  Financial: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-400",
  Other: "bg-gray-100 dark:bg-base-300 text-gray-800 dark:text-base-content/80",
};

function Redactions({
  caseRedactions = [],
  setCaseRedactions,
  caseId,
  editorContent,
  editor,
  isCaseLoading,
  highlightEnabled,
  setHighlightEnabled,
}: RedactionsProps) {
  const { reportPreferences, checkAuthStatus } = useAuth();
  const [isIdentifying, setIsIdentifying] = useState(false);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [isAddingCustom, setIsAddingCustom] = useState(false);
  const [customText, setCustomText] = useState("");
  const [customCategory, setCustomCategory] =
    useState<RedactionType["category"]>("Name");
  const [customReason, setCustomReason] = useState("");
  const [customTag, setCustomTag] = useState("");
  const [customStatute, setCustomStatute] = useState("");
  const [customJustification, setCustomJustification] = useState("");
  // Inline tag editing on a list item: which item is being edited + the draft
  // value. Tags label the black bar on export (e.g. "[suspect]") so readers
  // know what kind of information was removed.
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [tagDraft, setTagDraft] = useState("");
  // Inline statute/justification editing — these feed the "legal
  // justification" footnotes on export. Items sharing the same citation +
  // phrase share one footnote number, so keeping wording consistent matters.
  const [editingJustificationId, setEditingJustificationId] = useState<
    string | null
  >(null);
  const [statuteDraft, setStatuteDraft] = useState("");
  const [justificationDraft, setJustificationDraft] = useState("");
  // Set when the user cancels with Escape so the focusout that fires as the
  // inputs unmount doesn't save the discarded drafts.
  const justificationEscapedRef = useRef(false);
  // After editing one item's statute/justification, other items that shared
  // the exact same pair are offered a bulk update via a confirm dialog
  // (AI-generated justifications use identical wording per legal ground, so
  // edits usually apply to the whole group).
  const [pendingBulkUpdate, setPendingBulkUpdate] = useState<{
    ids: string[];
    statute?: string;
    justification?: string;
  } | null>(null);
  // Sidebar-level visibility for the per-item extras. Both default off so the
  // list stays compact; the toggles only appear (in a box under the master
  // toggle) while "Highlight redactions" is on. Persisted to localStorage so
  // the preference follows the user across cases.
  const readStoredToggle = (key: string) => {
    try {
      return JSON.parse(localStorage.getItem(key) || "false") === true;
    } catch {
      return false;
    }
  };
  const [showJustifications, setShowJustifications] = useState(() =>
    readStoredToggle("pnai-redactionsShowJustifications"),
  );
  const [showTags, setShowTags] = useState(() =>
    readStoredToggle("pnai-redactionsShowTags"),
  );
  useEffect(() => {
    localStorage.setItem(
      "pnai-redactionsShowJustifications",
      JSON.stringify(showJustifications),
    );
  }, [showJustifications]);
  useEffect(() => {
    localStorage.setItem("pnai-redactionsShowTags", JSON.stringify(showTags));
  }, [showTags]);
  const showJustificationsEffective = highlightEnabled && showJustifications;
  const showTagsEffective = highlightEnabled && showTags;

  // The extension's `enabled` flag is synced from the lifted state in the
  // parent. Here we only clear the focused item when highlighting turns off,
  // so toggling back on starts from a clean (unfocused) state.
  useEffect(() => {
    if (!highlightEnabled) {
      setFocusedId(null);
      editor?.commands.focusRedaction(null);
    }
  }, [highlightEnabled, editor]);

  const RedactionsSkeleton = () => (
    <div className="p-3 bg-base-200 rounded-lg">
      <div className="flex flex-col gap-2">
        <Skeleton width={120} height={14} />
        <Skeleton width="80%" height={14} />
      </div>
    </div>
  );

  // Focus a redaction in the editor: paints the matching range with the
  // focused mark and scrolls the first hit into view. Clicking when the
  // master toggle is off just turns the toggle on (and selects the item) so
  // the user sees something happen.
  const handleSelectRedaction = (redaction: RedactionType) => {
    if (!highlightEnabled) {
      setHighlightEnabled(true);
    }
    const nextId = focusedId === redaction.id ? null : redaction.id;
    setFocusedId(nextId);
    editor?.commands.focusRedaction(nextId);
    if (nextId) {
      // Defer one frame so the decoration is rendered before we query for it.
      requestAnimationFrame(() => {
        const target = document.querySelector(
          `.ProseMirror [data-redaction-id~="${redaction.id}"]`,
        ) as HTMLElement | null;
        if (!target) return;
        // Scroll ONLY the editor's own scroll container — not every scrollable
        // ancestor. Native scrollIntoView bubbles up to parent layout
        // containers (and the page), which on this layout leaves the editor
        // pane scrolled to a position the user can't scroll back from.
        const scrollContainer = target.closest(
          ".overflow-y-auto",
        ) as HTMLElement | null;
        if (scrollContainer) {
          const containerRect = scrollContainer.getBoundingClientRect();
          const targetRect = target.getBoundingClientRect();
          const offset =
            targetRect.top -
            containerRect.top -
            scrollContainer.clientHeight / 2 +
            targetRect.height / 2;
          scrollContainer.scrollBy({ top: offset, behavior: "smooth" });
        } else {
          target.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    }
  };

  // Replace the case's redactions array server-side. Updates local state on
  // success so the editor decoration set re-renders without a refetch.
  const persistRedactions = async (next: RedactionType[]) => {
    if (!caseId) return;
    try {
      const res = await axios.patch(
        `/api/cases/${caseId}/redactions`,
        { redactions: next },
        { withCredentials: true },
      );
      const saved = (res.data?.redactions as RedactionType[]) || next;
      setCaseRedactions?.(saved);
      editor?.commands.setRedactions(saved);
    } catch (err) {
      console.error("Error saving redactions:", err);
      checkAuthStatus();
      toast.error("Failed to save redactions");
    }
  };

  const handleRemove = async (id: string) => {
    const next = caseRedactions.filter((r) => r.id !== id);
    if (focusedId === id) {
      setFocusedId(null);
      editor?.commands.focusRedaction(null);
    }
    await persistRedactions(next);
  };

  const handleAddCustom = async () => {
    const trimmed = customText.trim();
    if (!trimmed) {
      toast.error("Enter the text you want to redact");
      return;
    }
    const newItem: RedactionType = {
      id:
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`,
      text: trimmed,
      category: customCategory,
      reason: customReason.trim() || undefined,
      tag: customTag.trim() || undefined,
      statute: customStatute.trim() || undefined,
      justification: customJustification.trim() || undefined,
      custom: true,
    };
    const next = [...caseRedactions, newItem];
    await persistRedactions(next);
    setCustomText("");
    setCustomReason("");
    setCustomTag("");
    setCustomStatute("");
    setCustomJustification("");
    setCustomCategory("Name");
    setIsAddingCustom(false);
  };

  // Save the tag draft for the item currently in tag-edit mode. An empty
  // draft clears the tag (the export falls back to a plain black bar).
  const handleSaveTag = async (id: string) => {
    const trimmed = tagDraft.trim();
    setEditingTagId(null);
    const next = caseRedactions.map((r) =>
      r.id === id ? { ...r, tag: trimmed || undefined } : r,
    );
    await persistRedactions(next);
  };

  // Save the statute/justification drafts. Clearing both removes the item
  // from the export footnotes entirely. If other items shared the edited
  // item's original statute + justification, offer to update them too.
  const handleSaveJustification = async (id: string) => {
    const statute = statuteDraft.trim();
    const justification = justificationDraft.trim();
    setEditingJustificationId(null);
    const original = caseRedactions.find((r) => r.id === id);
    const origStatute = original?.statute?.trim() || "";
    const origJustification = original?.justification?.trim() || "";
    const changed =
      statute !== origStatute || justification !== origJustification;
    const siblings =
      changed && (origStatute || origJustification)
        ? caseRedactions.filter(
            (r) =>
              r.id !== id &&
              (r.statute?.trim() || "") === origStatute &&
              (r.justification?.trim() || "") === origJustification,
          )
        : [];
    const next = caseRedactions.map((r) =>
      r.id === id
        ? {
            ...r,
            statute: statute || undefined,
            justification: justification || undefined,
          }
        : r,
    );
    await persistRedactions(next);
    if (siblings.length) {
      setPendingBulkUpdate({
        ids: siblings.map((s) => s.id),
        statute: statute || undefined,
        justification: justification || undefined,
      });
    }
  };

  // Apply the just-saved statute/justification to the sibling items the user
  // confirmed in the dialog.
  const applyBulkJustificationUpdate = async () => {
    if (!pendingBulkUpdate) return;
    const { ids, statute, justification } = pendingBulkUpdate;
    setPendingBulkUpdate(null);
    const next = caseRedactions.map((r) =>
      ids.includes(r.id) ? { ...r, statute, justification } : r,
    );
    await persistRedactions(next);
  };

  const startEditingJustification = (r: RedactionType) => {
    setStatuteDraft(r.statute || "");
    setJustificationDraft(r.justification || "");
    setEditingJustificationId(r.id);
  };

  const handleIdentify = async () => {
    if (!caseId) return;
    const toastId = toast.loading("Scanning report for PII...", {
      duration: Infinity,
    });
    try {
      setIsIdentifying(true);
      const response = await axios.post(
        "/api/redactions",
        {
          context: editorContent,
          reportPreferences,
          caseId,
        },
        { withCredentials: true },
      );
      const newRedactions =
        (response.data?.redactions as RedactionType[]) || [];
      setCaseRedactions?.(newRedactions);
      editor?.commands.setRedactions(newRedactions);
      // Turn the highlight on so the freshly identified items are visible in
      // the editor immediately — running a scan and seeing nothing change is
      // confusing when the master toggle happens to be off.
      setHighlightEnabled(true);
      toast.success(
        `Found ${newRedactions.length} item${newRedactions.length === 1 ? "" : "s"} to review`,
        { id: toastId, ...toastOptions },
      );
    } catch (error) {
      checkAuthStatus();
      console.error("Error identifying redactions:", error);
      toast.error("Failed to identify redactions", { id: toastId });
    } finally {
      setIsIdentifying(false);
    }
  };

  // Group redactions by category for a tidier sidebar; AI-flagged items keep
  // their original order within each group, custom items pin to the top.
  const sortedRedactions = [...caseRedactions].sort((a, b) => {
    if (!!a.custom !== !!b.custom) return a.custom ? -1 : 1;
    return a.category.localeCompare(b.category);
  });

  return (
    <div className="pt-2 md:pt-0 pb-40 md:pb-24">
      <div className={cn(isIdentifying && "opacity-35 pointer-events-none")}>
        <div className="flex items-center prose mb-2">
          <EyeSlashIcon className="size-5 text-blue-500 dark:text-blue-300" />
          <h3 className="m-0 ml-2 text-base">Redactions</h3>
          {caseRedactions.length > 0 && (
            <span className="ml-auto text-xs text-base-content/60">
              {caseRedactions.length} item
              {caseRedactions.length === 1 ? "" : "s"}
            </span>
          )}
        </div>

        {/* Master toggle — controls whether the report shows redaction
            highlights at all. Off by default; the report looks normal until
            the user turns this on. The "Export with redactions" checkbox in
            the export dialogs is a separate, independent setting. */}
        <div className="border border-base-300 rounded-lg p-3 mb-3 bg-base-100">
          <Toggle
            layout="compact"
            id="redactions-highlight"
            label="Highlight redactions"
            description="Preview which words will be blacked out. The report itself is not changed until you export with redactions."
            checked={highlightEnabled}
            onChange={(e) => setHighlightEnabled(e.target.checked)}
          />
        </div>

        {/* Per-item extras — only offered while highlighting is on. Both are
            off by default so the list stays compact; turning one on reveals
            the matching section on every redaction below. The box stays
            mounted so the 0fr→1fr grid-rows transition can animate it
            sliding open/closed when the master toggle flips. */}
        <div
          className={cn(
            "grid transition-all duration-300 ease-in-out",
            highlightEnabled
              ? "grid-rows-[1fr] opacity-100 mb-3"
              : "grid-rows-[0fr] opacity-0 mb-0 invisible",
          )}
          aria-hidden={!highlightEnabled}
        >
          <div className="overflow-hidden">
            <div className="border border-base-300 rounded-lg p-3 bg-base-100 grid gap-3">
              <Toggle
                layout="compact"
                id="redactions-justifications"
                label="Show legal justifications"
                description="Show and edit the government code and reason cited for each redaction."
                checked={showJustifications}
                onChange={(e) => setShowJustifications(e.target.checked)}
              />
              <div className="border-t border-base-200 pt-3">
                <Toggle
                  layout="compact"
                  id="redactions-tags"
                  label="Show redaction tags"
                  description="Show and edit the label printed inside each black bar on export (e.g. [suspect])."
                  checked={showTags}
                  onChange={(e) => setShowTags(e.target.checked)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 dark:bg-blue-900/40 p-3 rounded-lg mb-3">
          <p className="text-xs p-0 m-0">
            Click an item to jump to it. AI-flagged items may contain mistakes —
            review each one before relying on it.
          </p>
        </div>

        {/* Add custom redaction form */}
        {isAddingCustom ? (
          <div className="border border-base-300 rounded-lg p-3 mb-3 bg-base-100">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold m-0">
                Add custom redaction
              </h4>
              <button
                onClick={() => {
                  setIsAddingCustom(false);
                  setCustomText("");
                  setCustomReason("");
                  setCustomTag("");
                  setCustomStatute("");
                  setCustomJustification("");
                }}
                className="text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70"
                aria-label="Cancel"
              >
                <XMarkIcon className="size-4" />
              </button>
            </div>
            <input
              type="text"
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              placeholder="Text to redact (must match the report exactly)"
              className="w-full text-xs border border-base-300 rounded-md px-2 py-1.5 mb-2 focus:outline-none focus:border-blue-500"
              autoFocus
            />
            <select
              value={customCategory}
              onChange={(e) =>
                setCustomCategory(e.target.value as RedactionType["category"])
              }
              className="w-full text-xs border border-base-300 rounded-md px-2 py-1.5 mb-2 focus:outline-none focus:border-blue-500 bg-base-100"
            >
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              placeholder="Reason (optional)"
              className="w-full text-xs border border-base-300 rounded-md px-2 py-1.5 mb-2 focus:outline-none focus:border-blue-500"
            />
            <input
              type="text"
              value={customTag}
              onChange={(e) => setCustomTag(e.target.value)}
              placeholder='Tag shown on the exported document, e.g. "suspect" (optional)'
              className="w-full text-xs border border-base-300 rounded-md px-2 py-1.5 mb-2 focus:outline-none focus:border-blue-500"
            />
            <input
              type="text"
              value={customStatute}
              onChange={(e) => setCustomStatute(e.target.value)}
              placeholder='Statute, e.g. "16 M.R.S. §804(3)" (optional)'
              className="w-full text-xs border border-base-300 rounded-md px-2 py-1.5 mb-2 focus:outline-none focus:border-blue-500"
            />
            <input
              type="text"
              value={customJustification}
              onChange={(e) => setCustomJustification(e.target.value)}
              placeholder="Justification for the export footnote (optional)"
              className="w-full text-xs border border-base-300 rounded-md px-2 py-1.5 mb-2 focus:outline-none focus:border-blue-500"
            />
            <Button
              variant="primary"
              size="xs"
              className="w-full"
              onClick={handleAddCustom}
            >
              Add redaction
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            size="xs"
            className="w-full mb-3"
            onClick={() => setIsAddingCustom(true)}
            disabled={!caseId}
          >
            <PlusIcon className="size-4 mr-1" />
            Add custom redaction
          </Button>
        )}

        {/* Redaction list */}
        <div className="grid gap-y-2 mb-3">
          {isCaseLoading ? (
            Array.from({ length: 5 }, (_, i) => <RedactionsSkeleton key={i} />)
          ) : sortedRedactions.length > 0 ? (
            sortedRedactions.map((r) => (
              <div
                key={r.id}
                onClick={() => handleSelectRedaction(r)}
                className={cn(
                  "p-3 text-sm cursor-pointer transition border rounded-lg",
                  focusedId === r.id
                    ? "border-blue-400 bg-blue-50 dark:bg-blue-900/40 shadow-sm"
                    : "border-transparent bg-base-200 hover:bg-blue-50 dark:hover:bg-blue-900/40",
                )}
              >
                <div className="flex justify-between items-start gap-2 mb-1">
                  <span
                    className={cn(
                      "text-[10px] font-semibold px-2 py-0.5 rounded-full",
                      CATEGORY_COLORS[r.category] || CATEGORY_COLORS.Other,
                    )}
                  >
                    {r.category}
                    {r.custom && " · custom"}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(r.id);
                    }}
                    className="text-base-content/40 hover:text-red-500 shrink-0"
                    aria-label="Remove"
                  >
                    <TrashIcon className="size-3.5" />
                  </button>
                </div>
                <div className="text-xs font-medium text-base-content break-words line-clamp-2">
                  {r.text}
                </div>
                {r.reason && (
                  <p className="text-[11px] text-base-content/60 m-0 mt-1 line-clamp-2">
                    {r.reason}
                  </p>
                )}
                {/* Statutory justification — click to edit the citation and
                    footnote phrase. Items sharing identical wording share one
                    footnote number on export. */}
                {/* Both per-item sections stay mounted and slide open/closed
                    (0fr→1fr grid-rows) when their sidebar toggle flips —
                    same technique as the toggle box above. */}
                <div
                  className={cn(
                    "grid transition-all duration-300 ease-in-out",
                    showJustificationsEffective &&
                      (editingJustificationId === r.id ||
                        r.statute ||
                        r.justification)
                      ? "grid-rows-[1fr] opacity-100 mt-1.5"
                      : "grid-rows-[0fr] opacity-0 mt-0 invisible",
                  )}
                  aria-hidden={!showJustificationsEffective}
                >
                  <div className="overflow-hidden">
                    {editingJustificationId === r.id ? (
                      // Mirrors the display card's structure exactly — same
                      // joined two-row box, just with editable rows. Enter or
                      // clicking away saves; Escape discards.
                      <div
                        className="w-full overflow-hidden rounded-md border border-blue-400 bg-base-100"
                        onClick={(e) => e.stopPropagation()}
                        onBlur={(e) => {
                          if (e.currentTarget.contains(e.relatedTarget as Node))
                            return;
                          if (justificationEscapedRef.current) {
                            justificationEscapedRef.current = false;
                            return;
                          }
                          handleSaveJustification(r.id);
                        }}
                      >
                        <input
                          type="text"
                          value={statuteDraft}
                          onChange={(e) => setStatuteDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter")
                              handleSaveJustification(r.id);
                            if (e.key === "Escape") {
                              justificationEscapedRef.current = true;
                              setEditingJustificationId(null);
                            }
                          }}
                          placeholder='Statute, e.g. "16 M.R.S. §804(3)"'
                          className="block w-full border-0 border-b border-base-300 bg-base-200 px-2 py-1 text-[10px] font-medium text-gray-600 dark:text-base-content/70 focus:outline-none"
                          autoFocus
                        />
                        <textarea
                          value={justificationDraft}
                          // Auto-grow so a justification that wraps to multiple
                          // lines in the card is edited at that same height
                          // instead of scrolling inside a single-line input.
                          ref={(el) => {
                            if (el) {
                              el.style.height = "auto";
                              el.style.height = `${el.scrollHeight}px`;
                            }
                          }}
                          onChange={(e) => {
                            setJustificationDraft(e.target.value);
                            e.target.style.height = "auto";
                            e.target.style.height = `${e.target.scrollHeight}px`;
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleSaveJustification(r.id);
                            }
                            if (e.key === "Escape") {
                              justificationEscapedRef.current = true;
                              setEditingJustificationId(null);
                            }
                          }}
                          rows={1}
                          placeholder='Justification, e.g. "Constitutes an invasion of privacy"'
                          className="block w-full resize-none overflow-hidden border-0 px-2 py-1 text-[11px] text-base-content/60 focus:outline-none"
                        />
                      </div>
                    ) : (
                      (r.statute || r.justification) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditingJustification(r);
                          }}
                          className="group block w-full overflow-hidden rounded-md border border-gray-300 dark:border-base-300 bg-base-100 text-left hover:border-blue-400"
                          title="Edit the statute and justification cited in the footnotes of the exported narrative or document"
                        >
                          {r.statute && (
                            <span
                              className={cn(
                                "flex w-full items-center justify-between gap-1 bg-base-200 px-2 py-1 text-[10px] font-medium text-gray-600 dark:text-base-content/70 group-hover:text-blue-600 dark:group-hover:text-blue-300",
                                r.justification && "border-b border-base-300",
                              )}
                            >
                              {r.statute}
                              <PencilIcon className="size-2.5 shrink-0 opacity-0 group-hover:opacity-60" />
                            </span>
                          )}
                          {r.justification && (
                            <span className="flex w-full items-start justify-between gap-1 px-2 py-1 text-[11px] text-base-content/60 group-hover:text-gray-700 dark:group-hover:text-base-content/80">
                              <span className="line-clamp-2">
                                {r.justification}
                              </span>
                              {!r.statute && (
                                <PencilIcon className="size-2.5 mt-0.5 shrink-0 opacity-0 group-hover:opacity-60" />
                              )}
                            </span>
                          )}
                        </button>
                      )
                    )}
                  </div>
                </div>
                {/* Tag — the reader-facing label printed inside the black bar
                    on export. Click to edit; empty saves clear it. The chip
                    stays rendered while collapsed so unticking the sidebar
                    toggle slides it closed instead of popping it out. */}
                <div
                  className={cn(
                    "grid transition-all duration-300 ease-in-out",
                    showTagsEffective
                      ? "grid-rows-[1fr] opacity-100 mt-1.5"
                      : "grid-rows-[0fr] opacity-0 mt-0 invisible",
                  )}
                  aria-hidden={!showTagsEffective}
                >
                  <div className="overflow-hidden">
                    <div
                      className="flex flex-wrap items-center gap-1.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {editingTagId === r.id ? (
                        <input
                          type="text"
                          value={tagDraft}
                          onChange={(e) => setTagDraft(e.target.value)}
                          onBlur={() => handleSaveTag(r.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSaveTag(r.id);
                            if (e.key === "Escape") setEditingTagId(null);
                          }}
                          placeholder='e.g. "suspect"'
                          className="w-full text-[11px] border border-blue-400 rounded-md px-2 py-1 focus:outline-none"
                          autoFocus
                        />
                      ) : r.tag ? (
                        <button
                          onClick={() => {
                            setTagDraft(r.tag || "");
                            setEditingTagId(r.id);
                          }}
                          className="inline-flex items-center gap-1 bg-gray-900 text-white text-[10px] font-mono px-2 py-0.5 rounded hover:bg-gray-700"
                          title="Edit the label shown on the redaction bar in the exported narrative or document"
                        >
                          [{r.tag}]
                          <PencilIcon className="size-2.5 opacity-60" />
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            setTagDraft("");
                            setEditingTagId(r.id);
                          }}
                          className="inline-flex items-center gap-1 text-[10px] text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70 border border-dashed border-gray-300 dark:border-base-300 hover:border-gray-400 dark:hover:border-base-content/30 px-2 py-0.5 rounded"
                          title="Label the redaction bar in the exported narrative or document so readers know what was removed"
                        >
                          <PlusIcon className="size-2.5" />
                          Add tag
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {/* "Add justification" chip for items without one — same
                    always-mounted collapse treatment as the tag row. */}
                <div
                  className={cn(
                    "grid transition-all duration-300 ease-in-out",
                    showJustificationsEffective &&
                      !r.statute &&
                      !r.justification &&
                      editingJustificationId !== r.id
                      ? "grid-rows-[1fr] opacity-100 mt-1.5"
                      : "grid-rows-[0fr] opacity-0 mt-0 invisible",
                  )}
                  aria-hidden={!showJustificationsEffective}
                >
                  <div className="overflow-hidden">
                    <div onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => startEditingJustification(r)}
                        className="inline-flex items-center gap-1 text-[10px] text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70 border border-dashed border-gray-300 dark:border-base-300 hover:border-gray-400 dark:hover:border-base-content/30 px-2 py-0.5 rounded"
                        title="Cite the statute that justifies this redaction in the footnotes of the exported narrative or document"
                      >
                        <PlusIcon className="size-2.5" />
                        Add justification
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm my-2 text-gray-600 dark:text-base-content/70">
              No redactions yet. Run "Identify redactions" to scan the report
              for PII, or add custom items above.
            </p>
          )}
        </div>

        <Button
          variant="primary"
          className="w-full mb-4 md:mb-0"
          onClick={handleIdentify}
          loading={isIdentifying}
          disabled={isIdentifying || !caseId}
        >
          <ArrowPathIcon
            className={cn("size-4 mr-1", isIdentifying && "animate-spin")}
          />
          {caseRedactions.length > 0
            ? "Re-identify redactions"
            : "Identify redactions"}
        </Button>
      </div>

      {/* Bulk-update confirm — shown after editing one item's statute /
          justification when other items shared the exact same pair. */}
      {pendingBulkUpdate && (
        <dialog className="modal modal-open">
          <div className="modal-box max-w-sm">
            <h3 className="text-base font-semibold m-0">
              Update matching redactions?
            </h3>
            <p className="text-sm text-gray-600 dark:text-base-content/70 mt-2 mb-0">
              {pendingBulkUpdate.ids.length} other redaction
              {pendingBulkUpdate.ids.length === 1 ? "" : "s"} had the same
              statute and justification as the one you just edited. Apply your
              change to {pendingBulkUpdate.ids.length === 1 ? "it" : "them"} as
              well?
            </p>
            <div className="modal-action">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPendingBulkUpdate(null)}
              >
                Just this one
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={applyBulkJustificationUpdate}
              >
                Update {pendingBulkUpdate.ids.length} other
                {pendingBulkUpdate.ids.length === 1 ? "" : "s"}
              </Button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button onClick={() => setPendingBulkUpdate(null)}>close</button>
          </form>
        </dialog>
      )}
    </div>
  );
}

export default Redactions;
